import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchAccommodations } from '../lib/cms';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  useEffect(() => {
    async function loadData() {
      const page = await fetchPageBySlug('kontakt');
      setPageData(page);
    }
    loadData();
  }, []);

  // Pre-fill message if an accommodation unit is selected
  useEffect(() => {
    const enhedSlug = searchParams.get('enhed');
    if (enhedSlug) {
      fetchAccommodations().then((accs) => {
        const found = accs.find(a => a.slug?.da === enhedSlug || a.slug === enhedSlug);
        if (found) {
          const title = found.title?.da || found.title;
          setFormData(prev => ({
            ...prev,
            message: `Hej Campen Camping,\n\nJeg vil gerne forhøre mig om booking af "${title}".\n\nVenligst kontakt mig med information om ledighed, priser og det videre forløb.\n\nMed venlig hilsen`
          }));
        }
      });
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Client-side validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst alle påkrævede felter.',
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          submitting: false,
          success: true,
          error: null,
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: '',
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl ved indsendelsen. Prøv igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.',
      });
    }
  };

  const metaTitle = pageData?.meta_title?.da || "Kontakt os - Campen Camping";
  const metaDesc = pageData?.meta_description?.da || "Har du spørgsmål eller ønsker du at booke? Kontakt Campen Camping på Samsø. Ring på +45 5154 0023 eller skriv til info@campencamping.dk.";

  return (
    <div className="py-12 bg-stone-50 min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Intro */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
            Kontakt
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#0f0d0e] mt-1 mb-4">
            Skriv eller ring til os
          </h1>
          <p className="text-stone-600 text-sm md:text-base leading-relaxed">
            Har du spørgsmål til priser, ledige hytter, leje af hele pladsen eller noget helt fjerde? Vi sidder klar til at hjælpe dig.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Contact Info (5 Cols) */}
          <div className="lg:col-span-5 bg-white p-8 rounded-2xl border border-stone-200/60 shadow-sm flex flex-col gap-8">
            <div>
              <h2 className="text-2xl font-serif font-bold text-[#0f0d0e] mb-2">Kontaktoplysninger</h2>
              <p className="text-stone-500 text-sm">Vi bestræber os på at besvare alle henvendelser hurtigst muligt.</p>
            </div>

            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <div className="bg-[#67aedd]/10 text-[#67aedd] p-3.5 rounded-xl border border-[#67aedd]/20">
                  <i className="fa-solid fa-location-dot text-xl" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[#0f0d0e] text-base">Adresse</h4>
                  <p className="text-stone-600 text-sm mt-1">Vestermarksvej 72, 8305 Samsø</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-[#67aedd]/10 text-[#67aedd] p-3.5 rounded-xl border border-[#67aedd]/20">
                  <i className="fa-solid fa-phone text-xl" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[#0f0d0e] text-base">Telefon</h4>
                  <p className="text-stone-600 text-sm mt-1">
                    <a href="tel:+4551540023" className="hover:text-[#67aedd] transition-colors whitespace-nowrap">
                      +45 5154 0023
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-[#67aedd]/10 text-[#67aedd] p-3.5 rounded-xl border border-[#67aedd]/20">
                  <i className="fa-solid fa-envelope text-xl" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[#0f0d0e] text-base">E-mail</h4>
                  <p className="text-stone-600 text-sm mt-1">
                    <a href="mailto:info@campencamping.dk" className="hover:text-[#67aedd] transition-colors whitespace-nowrap">
                      info@campencamping.dk
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-stone-100 text-xs text-stone-500 flex flex-col gap-2">
              <p><span className="font-semibold text-stone-700">Firmanavn:</span> F Campen v/Finn Campen</p>
              <p><span className="font-semibold text-stone-700">CVR-nummer:</span> 33438516</p>
            </div>

            {/* Booking CTA inside Info */}
            <div className="bg-[#67aedd]/10 p-6 rounded-xl border border-[#67aedd]/20 text-center">
              <h4 className="font-serif font-bold text-[#0f0d0e] text-base mb-4">Du kan booke dit ophold direkte online.</h4>
              <Link
                to="/feriehuse"
                className="inline-block bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-2.5 rounded-full text-xs shadow-sm transition-colors"
              >
                Gå til Booking
              </Link>
            </div>
          </div>

          {/* Contact Form (7 Cols) */}
          <div className="lg:col-span-7 bg-white p-8 md:p-10 rounded-2xl border border-stone-200/60 shadow-sm">
            <h2 className="text-2xl font-serif font-bold text-[#0f0d0e] mb-6">Send os en besked</h2>

            {status.success ? (
              <div className="bg-[#67aedd]/10 border border-[#67aedd]/20 text-[#0f0d0e] p-6 rounded-2xl flex flex-col items-center text-center animate-scale-up">
                <div className="bg-[#67aedd]/20 text-[#67aedd] p-4 rounded-full text-3xl mb-4">
                  <i className="fa-solid fa-circle-check" />
                </div>
                <h3 className="font-serif font-bold text-lg">Tak for din besked!</h3>
                <p className="text-sm text-stone-600 mt-2 max-w-md">
                  Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt på din e-mail eller dit telefonnummer.
                </p>
                <button
                  onClick={() => setStatus({ submitting: false, success: false, error: null })}
                  className="mt-6 bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-2 rounded-full text-xs transition-colors"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Antispam Honeypot Field (Absolutely Hidden) */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL (Udfyld ikke dette felt)</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Name */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-stone-500">
                    Navn <span className="text-[#67aedd]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Dit fulde navn"
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-[#67aedd] focus:ring-1 focus:ring-[#67aedd] outline-none text-sm transition-all"
                  />
                </div>

                {/* Phone & Email Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Phone */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-stone-500">
                      Telefonnummer
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Dit telefonnummer (valgfrit)"
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-[#67aedd] focus:ring-1 focus:ring-[#67aedd] outline-none text-sm transition-all"
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-stone-500">
                      E-mail <span className="text-[#67aedd]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Din e-mailadresse"
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-[#67aedd] focus:ring-1 focus:ring-[#67aedd] outline-none text-sm transition-all"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-stone-500">
                    Beskrivelse / Besked <span className="text-[#67aedd]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Skriv din besked her..."
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-[#67aedd] focus:ring-1 focus:ring-[#67aedd] outline-none text-sm transition-all resize-none"
                  />
                </div>

                {/* Error Message */}
                {status.error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
                    <i className="fa-solid fa-triangle-exclamation text-base" />
                    <span>{status.error}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status.submitting}
                  className="w-full bg-[#67aedd] hover:bg-[#5aa1cc] disabled:bg-[#67aedd]/60 text-white font-bold py-3.5 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-sm flex items-center justify-center gap-2 cursor-pointer"
                >
                  {status.submitting ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin" /> Indsender...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane" /> Send besked
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
